import { toast } from '@cashfree-intl/coherent';
import { call, put, all, spawn, takeEvery } from 'redux-saga/effects';

// Constants
import {
  FETCH_WHITELISTING_DETAILS,
  GET_LIVENESS_DATA,
  GET_WHITELISTING_DATA,
  POST_WHITELISTING_DATA,
  LOADERS,
  CONTENT_TYPE_JSON_HEADER,
  UPDATE_WHITELISTING_DATA,
} from './constants';
import {
  CHECK_WEBSITE_LIVENESS_URL,
  FETCH_ONBOARDING_META_DATA_URL,
  GET_WHITELISTING_DATA_URL,
  POST_WHITELISTING_URL,
  UPDATE_WHITELISTING_URL,
} from 'constants/url';
// import { AUDIT_HEADER } from 'utils/constants';

// Actions
import { disableLoader, enableLoader } from './actions';

// Utils
import request from '../../utils/request';
import { showToast } from '../Toast/actions';

// Types
import {
  ActionType,
  AddToWhitelistPayloadType,
  AddToWhitelistResponseType,
  CheckLivenessPayloadType,
  CheckWebsiteLiveResponseType,
  FetchWhitelistingDetailsPayloadType,
  FetchWhitelistingPayloadType,
  OnboardingMetaDataResponseType,
  UpdateWhitelistingPayloadType,
  WhitelistTableResponseType,
} from './types';

// const DEVELOPERS_WHITELISTING_AUDIT = 'developer-whitelisting-audit';

export function* fetchWhitelisting({
  data: { successCb, errorCb },
}: ActionType<FetchWhitelistingPayloadType>) {
  try {
    yield put(enableLoader(LOADERS.PAGE_LOADER));
    const { url, method } = GET_WHITELISTING_DATA_URL;
    const response: WhitelistTableResponseType = yield call(request, url, {
      method,
      useBasePath: true,
    });
    successCb(response);
  } catch (err) {
    if (errorCb) errorCb();
    yield put(
      showToast(new Error(`Unable to fetch the data, Please try again later!`)),
    );
  } finally {
    yield put(disableLoader(LOADERS.PAGE_LOADER));
  }
}

export function* fetchWhitelistingDetails({
  data: { reqData, successCb },
}: ActionType<FetchWhitelistingDetailsPayloadType>) {
  try {
    yield put(enableLoader(LOADERS.PAGE_LOADER));
    const { method, url } = FETCH_ONBOARDING_META_DATA_URL;
    const options = {
      method,
      useBasePath: true,
      body: JSON.stringify(reqData),
    };
    const response: OnboardingMetaDataResponseType = yield call(
      request,
      url,
      options,
    );
    successCb(response);
  } catch (err) {
    toast.error(err?.errorMeta?.message);
  } finally {
    yield put(disableLoader(LOADERS.PAGE_LOADER));
  }
}

export function* addWhitelisting({
  data: { reqData, successCb, errorCb },
}: ActionType<AddToWhitelistPayloadType>) {
  try {
    yield put(enableLoader(LOADERS.MODAL_LOADER));
    const { url, method } = POST_WHITELISTING_URL;
    const options = {
      method,
      body: JSON.stringify(reqData),
      useBasePath: true,
    };
    const response:AddToWhitelistResponseType=yield call(request, url, options);
    if (successCb) successCb(response);
  } catch (err) {
    toast.error(err?.errorMeta?.message);
    if (errorCb) errorCb();
  } finally {
    yield put(disableLoader(LOADERS.MODAL_LOADER));
  }
}

export function* updateWhitelisting({
  data: { reqData, successCb },
}: ActionType<UpdateWhitelistingPayloadType>) {
  try {
    yield put(enableLoader(LOADERS.PAGE_LOADER));
    const { url, method } = UPDATE_WHITELISTING_URL;
    const options = {
      method,
      body: JSON.stringify(reqData),
      useBasePath: true,
    };
    yield call(request, url, options);
    if (successCb) successCb();
  } catch (err) {
    toast.error(err?.errorMeta?.message);
  } finally {
    yield put(disableLoader(LOADERS.PAGE_LOADER));
  }
}

export function* checkLiveness({
  data: { reqData, successCb },
}: ActionType<CheckLivenessPayloadType>) {
  try {
    yield put(enableLoader(LOADERS.MODAL_LOADER));
    const { url, method } = CHECK_WEBSITE_LIVENESS_URL;
    const options = {
      headers: CONTENT_TYPE_JSON_HEADER,
      method,
      queryString: {
        url: reqData.url,
      },
      useBasePath: true,
    };
    const response: CheckWebsiteLiveResponseType = yield call(
      request,
      url,
      options,
    );
    successCb(response?.isLive);
  } catch (err) {
    toast.error(err?.errorMeta?.message);
  } finally {
    yield put(disableLoader(LOADERS.MODAL_LOADER));
  }
}

export function* fetchWhitelistingSaga() {
  yield takeEvery(GET_WHITELISTING_DATA, fetchWhitelisting);
}

export function* addWhitelistingSaga() {
  yield takeEvery(POST_WHITELISTING_DATA, addWhitelisting);
}

export function* updateWhitelistingSaga() {
  yield takeEvery(UPDATE_WHITELISTING_DATA, updateWhitelisting);
}

export function* checkWebsiteLiveSaga() {
  yield takeEvery(GET_LIVENESS_DATA, checkLiveness);
}

export function* fetchWhitelistingDetailsSaga() {
  yield takeEvery(FETCH_WHITELISTING_DETAILS, fetchWhitelistingDetails);
}
// Individual exports for testing
export default function* whitelistingSaga() {
  // See example in containers/HomePage/saga.js
  const sagas = [
    fetchWhitelistingSaga,
    addWhitelistingSaga,
    checkWebsiteLiveSaga,
    fetchWhitelistingDetailsSaga,
    updateWhitelistingSaga,
  ];
  yield all(
    sagas.map(saga =>
      // eslint-disable-next-line func-names
      spawn(function*() {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
          }
        }
      }),
    ),
  );
}
